import { ReactNode, useEffect, useRef } from "react";
import { imagineStore } from "@/stores/imagine.store";
import {
  createRealtimeConnection,
  WebsocketChatTransport,
  type RealtimeConnection,
} from "@imagine/realtime/consumer";
import { getConsoleAuthToken } from "@/hooks/use-console-auth-token";
import { ImagineUIMessage } from "@imagine/ai-service/shared-types";
import { imagineConfigStore } from "@/stores/imagine-config.store";

export const ProjectRealtimeProvider = ({
  projectId,
  children,
}: {
  projectId: string;
  children: ReactNode;
}) => {
  const updateWorkspaceState = imagineStore.use.workspaceState.update();
  const resetWorkspaceState = imagineStore.use.workspaceState.reset();
  const setIsRealtimeConnected = imagineStore.use.setIsRealtimeConnected();
  const connectionRef = useRef<RealtimeConnection | null>(null);
  const transportRef = useRef<WebsocketChatTransport<ImagineUIMessage> | null>(
    null,
  );
  const isConnectingRef = useRef(false);

  const config = imagineConfigStore.getState().config;
  if (!config?.REALTIME_URI) {
    throw new Error("REALTIME_URI is not set");
  }

  useEffect(() => {
    // Prevent double connection in React StrictMode
    if (isConnectingRef.current || connectionRef.current) {
      console.log("[realtime] Already connecting or connected, skipping");
      return;
    }

    let cancelled = false;
    isConnectingRef.current = true;

    const connect = async () => {
      try {
        console.log("[realtime] Connecting to project:", projectId);
        const token = await getConsoleAuthToken();

        if (cancelled) {
          console.log("[realtime] Connection cancelled before completion");
          isConnectingRef.current = false;
          return;
        }

        // Create connection with event handlers
        const connection = await createRealtimeConnection({
          url: config.REALTIME_URI,
          projectId,
          token,
          onWorkspaceState: (state) => {
            if (state === null) {
              console.log("[realtime] workspace_state expired, resetting");
              resetWorkspaceState();
            } else {
              console.log("[realtime] workspace_state update:", state);
              updateWorkspaceState(state);
            }
          },
          onConnect: () => {
            console.log("[realtime] Connected");
            setIsRealtimeConnected(true);
          },
          onClose: () => {
            console.log("[realtime] Disconnected");
            setIsRealtimeConnected(false);
          },
          onError: (error) => {
            console.error("[realtime] Connection error:", error);
          },
        });

        if (cancelled) {
          console.log(
            "[realtime] Connection cancelled after creation, closing",
          );
          connection.close();
          isConnectingRef.current = false;
          return;
        }

        connectionRef.current = connection;
        isConnectingRef.current = false;

        // Create transport with the connected WebSocket
        const transport = new WebsocketChatTransport({
          ws: connection.ws,
          toolCallCallback: (result) => {
            console.log("[realtime] Tool call result:", result);
          },
        });

        transportRef.current = transport;
      } catch (error) {
        console.error("[realtime] Failed to connect:", error);
        isConnectingRef.current = false;
      }
    };

    connect();

    return () => {
      cancelled = true;
      if (connectionRef.current) {
        connectionRef.current.close();
        connectionRef.current = null;
      }
      if (transportRef.current) {
        transportRef.current.detach();
        transportRef.current = null;
      }
      isConnectingRef.current = false;
    };
  }, [
    projectId,
    updateWorkspaceState,
    resetWorkspaceState,
    setIsRealtimeConnected,
    config.REALTIME_URI,
  ]);

  return <>{children}</>;
};
